import { setViewModel } from './landingSlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import {
  validateRegno,
  validateSocialNumber,
  validateGovNumber,
  validateWeight,
} from './validators';
import { InputParams } from '../../components/BtInput';
import { NavigateFooterParams, NavigateParams } from '../../components/BtNavigate';
import { CONTRACT_CUSTOMER, MY_BOOKING_SCREEN_PATH } from '../../support/routeConstants';
import { RootState, store } from '../../app/store';
import { ButtonParams } from '../../components/BtButton';
import { LoadingParams } from '../../components/BtLoading';
import { useSelector } from 'react-redux';

export function none(logoUrl: string): LandingScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      body: '',
      govId: '',
      regno: '',
      next: '',
      private: '',
      company: '',
      registration: '',
      govNo: '',
      customerNumber: '',
      weight: '',
      maxWeight: '',
      weightBody: '',
      faq: '',
      support: '',
      booking: '',
      click: '',
      metaTitle: '',
      loading: '',
      terms: '',
      contractCustomer: '',
      customerUsp: '',
      customerUspBtn: '',
    },
    state: {
      govId: '',
      govNo: '',
      mobile: '',
      regno: '',
      weight: '',
      customerNumber: '',
      registered: false,
      isActive: 'person',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl ?? null,
      },
    },
  };
}

export namespace LandingScreen {
  interface Texts {
    title: string;
    body: string;
    govId: string;
    regno: string;
    next: string;
    private: string;
    company: string;
    registration: string;
    govNo: string;
    customerNumber: string;
    weight: string;
    maxWeight: string;
    weightBody: string;
    booking: string;
    faq: string;
    support: string;
    click: string;
    metaTitle: string;
    loading: string;
    terms: string;
    contractCustomer: string;
    customerUsp: string;
    customerUspBtn: string;
  }

  export interface State {
    govId: string;
    govNo: string;
    mobile: string;
    regno: string;
    weight: string;
    customerNumber: string;
    registered: boolean;
    isActive: ActiveViewModel;
  }

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Person {
    type: 'person';
    texts: Texts;
    state: State;
    greeting: string;
    body: string;
    govId: InputParams;
    regno: InputParams;
    registration: string;
    open: ButtonParams;
    weight: InputParams;
    next: ButtonParams;
    private: string;
    company: string;
    navigation: NavigateFooterParams[];
    close: ButtonParams;
    image: string;
    metaTitle: string;
    navigationTop: NavigateParams;
    terms: ButtonParams;
    customerUspBtn: ButtonParams;
    customerUsp: string;
    uspImage: string;
  }

  export interface Company {
    type: 'company';
    texts: Texts;
    state: State;
    greeting: string;
    body: string;
    regno: InputParams;
    weight: InputParams;
    next: ButtonParams;
    private: string;
    company: string;
    govNo: InputParams;
    registration: string;
    open: ButtonParams;
    customerNumber: InputParams;
    navigation: NavigateFooterParams[];
    close: ButtonParams;
    image: string;
    metaTitle: string;
    navigationTop: NavigateParams;
    terms: ButtonParams;
    customerUspBtn: ButtonParams;
    customerUsp: string;
    uspImage: string;
  }

  export interface Finished {
    type: 'finished';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    title: string;
    body: string;
    ok: ButtonParams;
    errorTitle: string;
  }

  export type ViewModel = None | Loading | Finished | Person | Company | Failed;

  export type ActiveViewModel = 'person' | 'company';

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isPerson(viewModel: ViewModel): viewModel is Person {
    return (viewModel as Person).type === 'person';
  }

  export function isCompany(viewModel: ViewModel): viewModel is Company {
    return (viewModel as Company).type === 'company';
  }
  export function isFinished(viewModel: ViewModel): viewModel is Finished {
    return (viewModel as Finished).type === 'finished';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isPerson(viewModel)) return viewModel.texts;
    if (isCompany(viewModel)) return viewModel.texts;
    if (isFinished(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    return {
      title: '',
      body: '',
      govId: '',
      regno: '',
      next: '',
      private: '',
      company: '',
      registration: '',
      govNo: '',
      customerNumber: '',
      weight: '',
      maxWeight: '',
      weightBody: '',
      booking: '',
      faq: '',
      support: '',
      click: '',
      metaTitle: '',
      loading: '',
      terms: '',
      contractCustomer: '',
      customerUsp: '',
      customerUspBtn: '',
    };
  }

  async function loading(viewModel: ViewModel, logoUrl: string): Promise<Loading> {
    const t = i18n.t;

    return {
      type: 'loading',
      texts: {
        title: t('landingScreen.title'),
        body: t('landingScreen.body'),
        govId: t('landingScreen.govId'),
        regno: t('landingScreen.regno'),
        next: t('landingScreen.next'),
        private: t('landingScreen.private'),
        company: t('landingScreen.company'),
        registration: t('landingScreen.registration'),
        govNo: t('landingScreen.govNo'),
        customerNumber: t('landingScreen.customerNumber'),
        weight: t('landingScreen.weight'),
        maxWeight: t('landingScreen.maxWeight'),
        weightBody: t('landingScreen.weightBody'),
        click: t('landingScreen.click'),
        booking: t('navigation.booking'),
        faq: t('navigation.faq'),
        support: t('navigation.support'),
        contractCustomer: t('navigation.contractCustomer'),
        metaTitle: t('landingScreen.metaTitle'),
        loading: t('loading.title'),
        terms: t('landingScreen.terms'),
        customerUsp: t('landingScreen.customerUsp'),
        customerUspBtn: t('landingScreen.customerUspBtn'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl ?? null,
        },
      },

      state: viewModel.state,
    };
  }

  async function person(
    viewModel: ViewModel,
    state: State,
    regnoError: string | null,
    govIdError: string | null,
    errorWeight: string | null,
    logoUrl: string | null,
  ): Promise<Person> {
    const texts = getTexts(viewModel);

    return {
      type: 'person',
      texts: texts,
      state: state,
      greeting: texts.title,
      body: texts.body,
      govId: {
        id: 'govId',
        name: 'govId',
        title: texts.govId,
        message: govIdError ? govIdError : '',
        disabled: false,
        value: state.govId,
        placeholder: '',
        type: 'text',
      },
      regno: {
        id: 'regno',
        name: 'regno',
        title: texts.regno,
        message: regnoError ? regnoError : '',
        disabled: state.registered,
        value: state.regno,
        placeholder: '',
        type: 'text',
      },
      weight: {
        id: 'weight',
        name: 'weight',
        title: texts.weight,
        message: errorWeight ? errorWeight : '',
        disabled: false,
        value: state.weight,
        placeholder: '',
        addOnRight: viewModel.texts.maxWeight,
        body: viewModel.texts.weightBody,
        type: 'number',
      },
      registration: texts.registration,
      open: { title: texts.click, image: null, visible: true },
      next: { title: texts.next, image: null, visible: true },
      private: texts.private,
      company: texts.company,
      navigation: [
        /* {
          title: texts.contractCustomer,
          image: 'ico_customer',
          path: CONTRACT_CUSTOMER,
        },*/
        {
          title: texts.booking,
          image: 'ico_my_booking',
          path: MY_BOOKING_SCREEN_PATH,
        },
        { title: texts.support, image: 'ico_support', path: '' },
      ],
      close: { title: '', image: 'ico_close', visible: true },
      image: 'ico_vehicle_transport',
      metaTitle: texts.metaTitle + texts.private,
      navigationTop: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl ?? null,
      },
      terms: { title: texts.terms, image: null, visible: true },
      customerUspBtn: {
        title: texts.customerUspBtn,
        image: null,
        visible: true,
      },
      customerUsp: texts.customerUsp,
      uspImage: 'ico_customer_md',
    };
  }

  function company(
    viewModel: ViewModel,
    state: State,
    regnoError: string | null,
    errorGovNo: string | null,
    errorWeight: string | null,
    logoUrl: string | null,
  ): Company {
    const texts = getTexts(viewModel);
    return {
      type: 'company',
      texts: texts,
      state: state,
      greeting: texts.title,
      body: texts.body,
      regno: {
        id: 'regno',
        name: 'regno',
        title: texts.regno,
        message: regnoError ? regnoError : '',
        disabled: state.registered,
        value: state.regno,
        placeholder: '',
        type: 'text',
      },
      weight: {
        id: 'weight',
        name: 'weight',
        title: texts.weight,
        message: errorWeight ? errorWeight : '',
        disabled: false,
        value: state.weight,
        placeholder: '',
        addOnRight: viewModel.texts.maxWeight,
        body: viewModel.texts.weightBody,
        type: 'number',
      },
      govNo: {
        id: 'govNo',
        name: 'govNo',
        title: texts.govNo,
        message: errorGovNo ? errorGovNo : '',
        disabled: false,
        value: state.govNo,
        placeholder: '',
        type: 'text',
      },
      customerNumber: {
        id: 'customerNumber',
        name: 'customerNumber',
        title: texts.customerNumber,
        message: '',
        disabled: false,
        value: state.customerNumber,
        placeholder: '',
        type: 'number',
      },
      registration: texts.registration,
      open: { title: texts.click, image: null, visible: true },
      next: { title: texts.next, image: null, visible: true },
      private: texts.private,
      company: texts.company,
      navigation: [
        /* {
          title: texts.contractCustomer,
          image: 'ico_customer',
          path: CONTRACT_CUSTOMER,
        },*/
        {
          title: texts.booking,
          image: 'ico_my_booking',
          path: MY_BOOKING_SCREEN_PATH,
        },
        { title: texts.support, image: 'ico_support', path: '' },
      ],
      close: { title: null, image: 'ico_close', visible: true },
      image: 'ico_vehicle_transport',
      metaTitle: texts.metaTitle + texts.company,
      navigationTop: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
      terms: { title: texts.terms, image: null, visible: true },
      customerUspBtn: {
        title: texts.customerUspBtn,
        image: null,
        visible: true,
      },
      customerUsp: texts.customerUsp,
      uspImage: 'ico_customer_md',
    };
  }

  function finished(viewModel: ViewModel, state: State, logoUrl: string): Finished {
    const texts = getTexts(viewModel);
    return {
      type: 'finished',
      texts: texts,
      state: state,
      loading: {
        title: texts.loading,
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
    };
  }

  function failed(viewModel: ViewModel, state: State): Failed {
    const texts = getTexts(viewModel);
    return {
      type: 'failed',
      texts: texts,
      state: state,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: true },
        image: '',
      },
      ok: { title: texts.next, image: null, visible: true },
      body: '',
      title: '',
      errorTitle: '',
    };
  }

  export function start(viewModel: ViewModel, logoUrl: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
      const loadingModel = await loading(viewModel, logoUrl);
      dispatch(setViewModel(loadingModel));
    };
  }

  export async function load(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    if (store.getState().appState.state.isActive === 'person') {
      return async (dispatch) => {
        dispatch(setViewModel(await person(viewModel, viewModel.state, null, null, null, logoUrl)));
      };
    } else {
      return (dispatch) => {
        dispatch(setViewModel(company(viewModel, viewModel.state, null, null, null, logoUrl)));
      };
    }
  }

  export async function registration(
    viewModel: ViewModel,
    registered: boolean,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const newState = { ...viewModel.state, registered: registered };
    return async (dispatch) => {
      if (viewModel.state.isActive === 'person') {
        dispatch(setViewModel(await person(viewModel, newState, null, null, null, logoUrl)));
      } else {
        dispatch(setViewModel(company(viewModel, newState, null, null, null, logoUrl)));
      }
    };
  }

  export async function changeToPerson(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const newState = {
      ...viewModel.state,
      isActive: 'person' as ActiveViewModel,
    };
    return async (dispatch) => {
      dispatch(setViewModel(await person(viewModel, newState, null, null, null, logoUrl)));
    };
  }

  export async function changeToCompany(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const newState = {
      ...viewModel.state,
      isActive: 'company' as ActiveViewModel,
    };
    return (dispatch) => {
      dispatch(setViewModel(company(viewModel, newState, null, null, null, logoUrl)));
    };
  }

  export async function reset(logoUrl: string): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return async (dispatch) => {
      dispatch(setViewModel(none(logoUrl)));
      dispatch(
        setAppState({
          ...store.getState().appState.state,
          govId: '',
          govNo: '',
          mobile: '',
          regno: '',
          registered: false,
          weight: '',
          isActive: 'person',
          address: '',
          zipCode: '',
          city: '',
          sessionActive: false,
          sourceLatitude: '',
          sourceLongitude: '',
          sourceAddress: '',
          destinationLatitude: '',
          destinationLongitude: '',
          destinationAddress: '',
          missionDate: '',
          missionTime: '',
          missionPrice: '',
          vehicleType: '',
          customerName: '',
          id: '',
          createCustomerGovNo: '',
          createCustomerEmail: '',
          createCustomerPhone: '',
          customerNumber: '',
        }),
      );
    };
  }

  function FormatGovId(id: string): string {
    const first = parseInt(id.substring(0, 2));

    const add = first <= 99 && first >= 30 ? '19' : '20';

    return add + id;
  }

  export async function login(
    viewModel: ViewModel,
    props: {
      govId: string;
      weight: string;
      regno: string;
      customerNumber: string;
      govNo: string;
    },
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const regnoError = !viewModel.state.registered ? validateRegno(props.regno) : null;
    const weightError = viewModel.state.registered ? validateWeight(props.weight) : null;
    const newState = {
      ...viewModel.state,
      govId: props.govId,
      weight: props.weight,
      regno: props.regno,
      customerNumber: props.customerNumber,
      govNo: props.govNo,
    };

    if (viewModel.state.isActive === 'company') {
      const govNoError = validateGovNumber(props.govNo);

      if (govNoError === null && weightError === null && regnoError === null) {
        return (dispatch) => {
          dispatch(setViewModel(finished(viewModel, newState, logoUrl)));
          dispatch(
            setAppState({
              ...store.getState().appState.state,
              govId: '',
              weight: viewModel.state.registered ? props.weight : '',
              regno: viewModel.state.registered ? '' : props.regno,
              govNo: props.govNo.replaceAll('-', ''),
              registered: !viewModel.state.registered,
              isActive: viewModel.state.isActive,
              sessionActive: true,
              customerNumber: props.customerNumber,
            }),
          );
        };
      } else {
        return (dispatch) => {
          dispatch(
            setViewModel(
              company(viewModel, newState, regnoError, govNoError, weightError, logoUrl),
            ),
          );
        };
      }
    } else {
      const govIdError = validateSocialNumber(props.govId);

      if (govIdError === null && weightError === null && regnoError === null) {
        const foramatedGovId =
          props.govId.substring(0, 2) !== '19' && props.govId.substring(0, 2) !== '20'
            ? FormatGovId(props.govId).replaceAll('-', '')
            : props.govId.replaceAll('-', '');

        return (dispatch) => {
          dispatch(setViewModel(finished(viewModel, newState, logoUrl)));
          dispatch(
            setAppState({
              ...store.getState().appState.state,
              govId: foramatedGovId,
              weight: viewModel.state.registered ? props.weight : '',
              regno: viewModel.state.registered ? '' : props.regno,
              govNo: '',
              registered: !viewModel.state.registered,
              isActive: viewModel.state.isActive,
              sessionActive: true,
            }),
          );
        };
      } else {
        return async (dispatch) => {
          dispatch(
            setViewModel(
              await person(viewModel, newState, regnoError, govIdError, weightError, logoUrl),
            ),
          );
        };
      }
    }
  }

  export async function save(
    viewModel: ViewModel,
    logoUrl: string,
    props: {
      govId: string;
      weight: string;
      regno: string;
      customerNumber: string;
      govNo: string;
    },
    isActive?: ActiveViewModel,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const newActive = isActive !== undefined ? isActive : viewModel.state.isActive;
    const newState = {
      ...viewModel.state,
      govId: props.govId,
      weight: props.weight,
      regno: props.regno,
      customerNumber: props.customerNumber,
      govNo: props.govNo,
      isActive: newActive,
    };

    if (viewModel.state.isActive === 'company' || isActive === 'company') {
      return (dispatch) => {
        dispatch(setViewModel(company(viewModel, newState, null, null, null, logoUrl)));
      };
    } else {
      return async (dispatch) => {
        dispatch(setViewModel(await person(viewModel, newState, null, null, null, logoUrl)));
      };
    }
  }
}
